import axiosInstance from '../utils/axiosConfig';

/**
 * HMS AI Document Naming Service
 * ================================
 *
 * Thin wrapper around the /api/document-naming/ endpoints. Used by the
 * shared DocumentNameSuggestionForm component (between file pick and
 * upload submit) and by every list view that wants to show the
 * AI-generated display name instead of the raw filename.
 *
 * The naming layer is HMS-side only - DocuWare itself is never
 * modified. If a row does not exist for a given DocuWare document ID
 * (e.g. legacy docs uploaded before this feature shipped), the lookup
 * endpoint simply omits it from the result map and the caller is
 * expected to fall back to the DocuWare SUBJECT field.
 */

const BASE = '/api/document-naming';

/**
 * Build a multipart suggestion payload (file + module + context).
 * Returns the suggestion object matching the backend NameSuggestion
 * dataclass: { display_name, component_*, extraction_confidence, warnings }.
 */
export const suggestName = async ({
  file,
  module,
  relatedEntityId = '',
  typeDropdownValue = '',
  partnerName = '',
  kycSubtype = '',
  documentDate = '',
  documentNumber = '',
  skipExtraction = false,
} = {}) => {
  const formData = new FormData();
  if (file) {
    formData.append('file', file);
  }
  formData.append('module', module);
  if (relatedEntityId) formData.append('related_entity_id', String(relatedEntityId));
  if (typeDropdownValue) formData.append('type_dropdown_value', typeDropdownValue);
  if (partnerName) formData.append('partner_name', partnerName);
  if (kycSubtype) formData.append('kyc_subtype', kycSubtype);
  if (documentDate) formData.append('document_date', documentDate);
  if (documentNumber) formData.append('document_number', documentNumber);
  if (skipExtraction) formData.append('skip_extraction', 'true');

  const response = await axiosInstance.post(`${BASE}/suggest/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

/**
 * Persist the AI display name for an uploaded document.
 *
 * Called AFTER the existing DocuWare/Django upload flow has returned
 * a docuware_document_id. Idempotent on docuware_document_id: a
 * second call updates the existing row.
 *
 * Accepts BOTH snake_case keys (matching the wire format) and camelCase
 * keys for ergonomic interop with both backend-aligned and JS-idiomatic
 * call sites.
 */
export const confirmName = async (input = {}) => {
  // Saved first, named after (ClickUp 1248h53bmhy, Chris's call 3): an upload
  // box that still has the file hands it over, and the name is saved at once
  // (what the person typed, else the original filename) while the reader runs
  // in the background. Boxes without the file keep the old door.
  if (input.file) {
    return saveAndRead(input);
  }
  const docuwareId = input.docuwareDocumentId ?? input.docuware_document_id;
  const module = input.module;
  const relatedEntityId = input.relatedEntityId ?? input.related_entity_id ?? '';
  const originalFilename = input.originalFilename ?? input.original_filename ?? '';
  const components = input.components || {};
  const aiSuggestion = input.aiSuggestion ?? input.ai_suggestion ?? {};
  const userOverrides = input.userOverrides ?? input.user_overrides ?? [];
  const extractionConfidence = input.extractionConfidence ?? input.extraction_confidence ?? null;
  const isUserConfirmed = input.isUserConfirmed ?? input.is_user_confirmed ?? true;

  const response = await axiosInstance.post(`${BASE}/confirm/`, {
    docuware_document_id: String(docuwareId || ''),
    module,
    related_entity_id: String(relatedEntityId || ''),
    original_filename: originalFilename || '',
    components,
    ai_suggestion: aiSuggestion,
    user_overrides: userOverrides,
    extraction_confidence: extractionConfidence,
    is_user_confirmed: isUserConfirmed,
  });
  return response.data;
};

/**
 * Save the document's name NOW and have the reader suggest one later.
 *
 * Called right after the upload has returned its DocuWare document id, with
 * the same file. Answers at once; the list shows "Suggested name: ... Use it"
 * when the suggestion arrives (see SuggestedName + useSuggestionPolling).
 */
export const saveAndRead = async (input = {}) => {
  const formData = new FormData();
  formData.append('docuware_document_id', String(input.docuwareDocumentId ?? input.docuware_document_id ?? ''));
  formData.append('module', input.module);
  formData.append('related_entity_id', String(input.relatedEntityId ?? input.related_entity_id ?? ''));
  formData.append('original_filename', input.originalFilename ?? input.original_filename ?? input.file?.name ?? '');
  formData.append('file', input.file);
  if (input.typeDropdownValue) formData.append('type_dropdown_value', input.typeDropdownValue);
  if (input.partnerName) formData.append('partner_name', input.partnerName);
  if (input.kycSubtype) formData.append('kyc_subtype', input.kycSubtype);
  const typed = input.components || {};
  if (Object.values(typed).some((value) => value && String(value).trim())) {
    formData.append('components', JSON.stringify(typed));
  }
  const response = await axiosInstance.post(`${BASE}/save-and-read/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

/** The one click: the suggested name becomes the document's name. */
export const takeSuggestion = async (docuwareDocumentId) => {
  const response = await axiosInstance.post(
    `${BASE}/suggestion/${encodeURIComponent(docuwareDocumentId)}/`,
    { action: 'take' },
  );
  return response.data;
};

/** Keep the current name; the suggestion line goes away. */
export const dismissSuggestion = async (docuwareDocumentId) => {
  const response = await axiosInstance.post(
    `${BASE}/suggestion/${encodeURIComponent(docuwareDocumentId)}/`,
    { action: 'dismiss' },
  );
  return response.data;
};

/** True while the reader is still working on this document's name. */
export const isReadingForName = (row) => row?.suggestion_status === 'reading';

/**
 * Bulk lookup display names for a list of DocuWare document IDs.
 *
 * Returns a map keyed by docuware_document_id. IDs that have no row
 * are omitted from the response - the caller falls back to the
 * existing DocuWare SUBJECT in that case.
 */
export const lookupNames = async (ids = [], moduleFilter = '') => {
  const cleanIds = (ids || [])
    .filter(Boolean)
    .map((id) => String(id));
  if (cleanIds.length === 0) {
    return {};
  }
  const params = new URLSearchParams();
  params.set('ids', cleanIds.join(','));
  if (moduleFilter) params.set('module', moduleFilter);
  const response = await axiosInstance.get(`${BASE}/lookup/?${params.toString()}`);
  return response.data?.results || {};
};

/**
 * Edit a previously-confirmed display name (e.g. inline edit on a list).
 */
export const updateName = async (docuwareDocumentId, components, userOverrides = []) => {
  const response = await axiosInstance.patch(
    `${BASE}/${encodeURIComponent(docuwareDocumentId)}/`,
    {
      components,
      user_overrides: userOverrides,
    }
  );
  return response.data;
};

const documentNamingService = {
  suggestName,
  confirmName,
  saveAndRead,
  takeSuggestion,
  dismissSuggestion,
  isReadingForName,
  lookupNames,
  updateName,
};

export default documentNamingService;
